<script setup>
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { reactive, ref, watch } from 'vue';
import { array, object, string } from 'yup';

const { tenant, eCommerceProducts, aiProductImage, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    eCommerceProducts: {
        type: [Array, Function],
        required: true,
    },
    aiProductImage: {
        type: Object,
        required: false,
    },
    callback: {
        type: Function,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const isOpen = ref(false);

const loading = ref(true);

const aiProductImageAttachments = ref([]);

const innerECommerceProducts = ref(
    typeof eCommerceProducts === 'function' ? [] : eCommerceProducts,
);

const form = reactive({
    additionalInstructions: aiProductImage
        ? aiProductImage.additionalInstructions
        : '',
    aiProductImageAttachments: [],
});

const validationSchema = object({
    additionalInstructions: string().nullable(),
    aiProductImageAttachments: array().of(object()).nullable(),
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(
            route('front.projectManagement.ai.getProductImageAttachments', {
                tenant: tenant.id,
            }),
        ),
        ...(typeof eCommerceProducts === 'function'
            ? [Promise.resolve(eCommerceProducts())]
            : []),
    ])
        .then((response) => {
            aiProductImageAttachments.value = response[0].data;

            if (aiProductImage) {
                form.aiProductImageAttachments =
                    aiProductImageAttachments.value.filter((attachment) =>
                        aiProductImage.aiProductImageAttachmentIds.includes(
                            attachment.id,
                        ),
                    );
            }

            if (typeof eCommerceProducts === 'function') {
                innerECommerceProducts.value = response[1];
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.ai.postScheduleProductImageGeneration',
                {
                    tenant: tenant.id,
                },
            ),
            {
                cmsProductIds: innerECommerceProducts.value.map(
                    (product) => product.cmsProductId,
                ),
                additionalInstructions: form.additionalInstructions,
                aiProductImageAttachmentIds: form.aiProductImageAttachments.map(
                    (attachment) => attachment.id,
                ),
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(
    () => isOpen.value,
    (newValue) => {
        if (newValue) {
            updateData();
        } else {
            loading.value = true;
        }
    },
);
</script>

<template>
    <DialogModal v-model="isOpen" :loading="loading">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            Programmer la génération d'images pour
            {{ innerECommerceProducts.length }} produit(s)
        </template>

        <template #full-content>
            <FormRoot
                v-slot="{ errors }"
                :validation-schema="validationSchema"
                :submit="submit"
            >
                <FormColumn>
                    <FormContent>
                        <div class="wysiwyg mb-4">
                            <ul>
                                <li>
                                    Utiliser <b>product_image_to_edit</b> pour
                                    faire référence à l'image du produit de base
                                    en cours d'édition.
                                </li>
                                <li>
                                    Utiliser <b>le nom de la pièce jointe</b>
                                    pour faire référence à une pièce jointe
                                    spécifique.
                                </li>
                            </ul>
                        </div>

                        <TextareaField
                            v-model="form.additionalInstructions"
                            field-name="additionalInstructions"
                            field-id="additionalInstructions"
                            label="Instructions supplémentaires"
                            rows="8"
                        />

                        <FormSeparator />

                        <ComboboxField
                            v-model="form.aiProductImageAttachments"
                            :options="aiProductImageAttachments"
                            label="Pièce(s) jointe(s) à utiliser"
                            field-name="aiProductImageAttachments"
                            field-id="aiProductImageAttachments"
                            filtered-option-key="name"
                            multiple
                            has-empty-option
                            by="id"
                        >
                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ComboboxField>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                    submit-text="Programmer"
                    submitting-text="Programmation en cours..."
                />
            </FormRoot>
        </template>
    </DialogModal>
</template>
